<template>
    <div>
        <div v-if="!submissionSuccess && !removalRequested" class="c-card__content">

            <h2>{{$t(i18n.form.mutation.form_title)}}</h2>

            <form class="c-form c-form--stacked" @submit.prevent="submit">
                <alert-box-danger v-show="selfModificationError">
                    <p>{{$t(i18n.form.alert_box.self_modification)}}</p>
                </alert-box-danger>
                <alert-box-danger v-show="generalError">
                    <p>{{$t(i18n.form.alert_box.general)}}</p>
                </alert-box-danger>
                <email-input :validation-state="$v.form.email"
                             :title="$t(i18n.form.email_input_label)"
                             :placeholder="$t(i18n.form.email_placeholder)"
                             :pre-filled-value="form.email"
                             v-model="form.email"/>

                <radio-buttons group="rol-medewerker"
                               :items="radioRoles"
                               :title="$t(i18n.form.role_selection_radios_label)"
                               :validation-state="$v.form.selectedRole"
                               v-model="form.selectedRole"/>

                <div class="c-form__row">
                    <div class="c-form__submit">
                        <button type="submit" class="o-button o-button--wide">{{$t(i18n.form.submit_label)}}</button>
                        <button @click="showConfirmDeleteEmployee" class="o-button o-button--wide o-button--clear o-button--cancel">{{$t(i18n.form.remove_employee_label)}}</button>
                    </div>
                </div>
            </form>
        </div>
        <employee-administration-success :form="form"
                                   :title="$t(i18n.form.mutation.success_text.title)"
                                   :email-text="$t(i18n.form.mutation.success_text.email)"
                                   :role-text="$t(i18n.form.mutation.success_text.role)"
                                   :general-text="$t(i18n.form.mutation.success_text.general)"
                                   :back-to-overview-text="$t(i18n.form.back_to_overview)"
                                   v-if="submissionSuccess"/>
        <employee-removal-confirm :form="form" :employee-id="employeeId" v-on:confirm-removal="submitDeleteEmployee" v-if="removalRequested"/>
    </div>
</template>

<script>
    import {
        email,
        required
    } from 'vuelidate/lib/validators'
    import {Utils} from "@bvm/core";
    import {ROLE_KEY} from "../../constants/constants";
    import {deleteEmployee, getEmployeeById, mutateEmployee} from "../../api/employee/employee-api";
    import {goToSpecialPage} from "@bvm/core/src/api/specialpage/specialpage.api";

    const i18nBuilder = new Utils.I18n.I18nBuilder('employee')
        .add('overview_role_employee')
        .add('overview_role_administrator')
        .add('form', b => b.add('email_input_label')
            .add('alert_box', b => b.add('self_modification').add('general'))
            .add('email_placeholder')
            .add('role_selection_radios_label')
            .add('employee_form_remove_employee_button_title')
            .add('submit_label')
            .add('remove_employee_label')
            .add('back_to_overview')
            .add('mutation', b => b.add('form_title')
                .add('success_text', b => b.add('title')
                    .add('email')
                    .add('role')
                    .add('general'))));
    export const messageKeys = i18nBuilder.getKeys();

    export default {
        name: "EmployeeMutationForm",
        components: {
            'alert-box-danger': () => import('../fragment/AlertBoxDanger'),
            'employee-removal-confirm': () => import("./EmployeeRemovalConfirm"),
            'employee-administration-success': () => import('./EmployeeAdministrationSuccess'),
            'radio-buttons': () => import('../fragment/RadioButtons'),
            'email-input': () => import('../fragment/EmailInput')
        },
        data() {
            return {
                i18n: i18nBuilder.getData(),
                form: {
                    email: null,
                    selectedRole: {} // {key, value}
                },
                employeeId: null,
                submissionSuccess: false,
                removalRequested: false,
                selfModificationError: false,
                generalError: false,
                processingSubmission: false,
                selectedRole: ''
            }
        },
        created() {
            this.employeeId = Number(Utils.Url.getQueryParameters().id);
            getEmployeeById(this.employeeId)
                .then(employeeDetails => {
                    this.form.email = employeeDetails.data.emailAddress;
                    this.selectedRole = employeeDetails.data.role;
                }).catch(() => {
                    this.generalError = true;
                });
        },
        computed: {
            radioRoles() {
                let roles = [
                    {key: ROLE_KEY.EMPLOYEE, value: this.$t(this.i18n.overview_role_employee)},
                    {key: ROLE_KEY.ADMINISTRATOR, value: this.$t(this.i18n.overview_role_administrator)}
                ];

                // Set the employee role received from the backend to the form
                // Required due to the loading time of translations
                roles.forEach(role => {
                    if (role.key === this.selectedRole) {
                        this.form.selectedRole = role;
                    }
                });

                return roles;
            }
        },
        methods: {
            submit() {
                const vm = this;
                vm.$v.form.$touch();
                if (!vm.processingSubmission && !vm.$v.form.$invalid && !vm.selfModificationError) {
                    vm.processingSubmission = true;
                    let employee = {
                        id: vm.employeeId,
                        emailAddress: vm.form.email,
                        role: vm.form.selectedRole.key
                    };
                    mutateEmployee(employee)
                        .then(function(response) {
                            vm.selfModificationError = response.resultCode === 405;
                            if(vm.selfModificationError === false){
                                vm.submissionSuccess = true;
                            }
                        }).catch(() => {
                            vm.generalError = true;
                        }).finally(() =>
                            vm.processingSubmission = false
                        )
                }
            },
            submitDeleteEmployee() {
                const vm = this;
                if (!vm.processingSubmission) {
                    let employee = {
                        id: vm.employeeId,
                        emailAddress: vm.form.email,
                        role: vm.form.selectedRole.value
                    }
                    vm.processingSubmission = true;
                    deleteEmployee(employee)
                        .then(function(response) {
                            vm.selfModificationError = response.resultCode === 405;
                            if(vm.selfModificationError === false){
                                goToSpecialPage("ADMIN_OVERVIEW");
                            }
                        })
                        .catch(() => {
                            vm.generalError = true;
                        }).finally(() => {
                            vm.removalRequested = false;
                            vm.processingSubmission = false;
                        });
                }
            },
            showConfirmDeleteEmployee() {
                this.$v.form.$touch();
                if (!this.selfModificationError && !this.$v.form.$invalid) {
                    this.removalRequested = true;
                }
            }
        },
        validations: {
            form: {
                email: {required, email},
                selectedRole: {required}
            }
        }
    }
</script>
